import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Typography, TextField, Drawer, Box, Button } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Local files
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useFifthAnalyses from 'hooks/useFifthAnalyses';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { fifthAnalysesSelectors } from 'features/fifthAnalyses';

const FifthAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { fifth_analysis_id } = useParams();
  const { getFifthAnalysis, clearLocalActiveFifthAnalysis } = useFifthAnalyses();
  const { getFifthAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const fifthAnalysisFetching = useCustomSelector(fifthAnalysesSelectors.fifthAnalysisIsFetching);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [result, setResult] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [firstTimeEventsOpened, setFirstTimeEventsOpened] = useState(true);

  const fetchFifthAnalysis = useCallback((id) => {
    setOpen(true);
    getFifthAnalysis({ id })
    .then(({ payload, error }) => {
      if (!error) {
        const { data: { fifth_analysis } } = payload;
        handleSetData(fifth_analysis);
      }
    })
  }, [getFifthAnalysis]);

  const handleSetData = (fifth_analysis) => {
    setInput(fifth_analysis?.input);
    setResult(fifth_analysis?.result);
  };

  const handleExiting = () => {
    setInput('');
    setResult('');
    setExpanded(false);
    setFirstTimeEventsOpened(true);
    navigate(`/flow/fl55d`);
    clearLocalActiveFifthAnalysis();
    clearLocalAnalysesEvents();
  };

  const handleClose = () => setOpen(false);

  const fetchFifthAnalysesEvents = useCallback((offset, fifth_analysis_id) => {
    getFifthAnalysesEvents({ offset, fifth_analysis_id })
  }, [ getFifthAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (firstTimeEventsOpened) {
      fetchFifthAnalysesEvents(0, fifth_analysis_id);
      setFirstTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!fifth_analysis_id) fetchFifthAnalysis(fifth_analysis_id);
  }, [fifth_analysis_id, fetchFifthAnalysis]);

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
      { fifthAnalysisFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {fifth_analysis_id}</Typography>
          <TextField
            fullWidth
            margin='normal'
            label='Input'
            multiline
            minRows={5}
            maxRows={8}
            disabled
            value={input ?? ''}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#073842",
              }
            }}
          />
          <TextField
            fullWidth
            margin='normal'
            label='Result'
            multiline
            minRows={5}
            maxRows={8}
            disabled
            value={result ?? ''}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#073842",
              }
            }}
          />
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={fifth_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchFifthAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
        </>}
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <FifthAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);